/* Single source of truth for the running round: one store, one data source,
   started once for the lifetime of the app. */

import { useEffect } from 'react'
import { RUNTIME } from '../config/runtime'
import { createStore, useStoreSnapshot } from '../core/store'
import type { CompetitionState } from '../core/types'
import type { DataSource } from './DataSource'
import { LiveDataSource } from './live/LiveDataSource'
import { MockDataSource } from './mock/MockDataSource'
import { emptyState } from './state'

export const competitionStore = createStore<CompetitionState>(emptyState())

function createDataSource(): DataSource {
  return RUNTIME.mode === 'live'
    ? new LiveDataSource(competitionStore)
    : new MockDataSource(competitionStore)
}

let source: DataSource | null = null
let mounted = 0

/** Starts the feed on first mount and tears it down when the last user leaves. */
export function useCompetitionFeed(): void {
  useEffect(() => {
    mounted += 1
    if (!source) {
      source = createDataSource()
      source.start()
      // ?autostart=<operator key> skips standby, for kiosk screens. Same key
      // as the console, so it is not a way around the gate.
      if (new URLSearchParams(window.location.search).get('autostart') === RUNTIME.adminKey) {
        source.activate()
      }
    }
    return () => {
      mounted -= 1
      if (mounted === 0) {
        source?.stop()
        source = null
      }
    }
  }, [])
}

/** Open the round. Wired to the one button that starts the competition. */
export function activateCompetition(): void {
  source?.activate()
}

/** The whole snapshot. Re-renders once per feed tick, never per field. */
export function useCompetition(): CompetitionState {
  return useStoreSnapshot(competitionStore)
}
